import { authSlice as api } from './index'
const injectedRtkApi = api.injectEndpoints({
    endpoints: (build) => ({
        login: build.mutation<LoginApiResponse, LoginApiArg>({
            query: (queryArg) => ({
                url: '/api/sso/login',
                method: 'POST',
                body: queryArg.body,
            }),
        }),
        refreshToken: build.query<RefreshTokenApiResponse, RefreshTokenApiArg>({
            query: (queryArg) => ({
                url: '/api/sso/token/refresh',
                params: { refreshToken: queryArg.refreshToken },
            }),
        }),
        invalidateToken: build.query<
            InvalidateTokenApiResponse,
            InvalidateTokenApiArg
        >({
            query: (queryArg) => ({
                url: '/api/sso/token/invalidate',
                params: { refreshToken: queryArg.refreshToken },
            }),
        }),
        userRegister: build.mutation<
            UserRegisterApiResponse,
            UserRegisterApiArg
        >({
            query: (queryArg) => ({
                url: '/api/sso/user/register',
                method: 'POST',
                body: queryArg.registerUserCommand,
            }),
        }),
        getUserRegisterConfirm: build.query<
            GetUserRegisterConfirmApiResponse,
            GetUserRegisterConfirmApiArg
        >({
            query: (queryArg) => ({
                url: `/api/sso/user/register/confirm/${queryArg.token}`,
                params: { lang: queryArg.lang },
            }),
        }),
        getUserSession: build.query<
            GetUserSessionApiResponse,
            GetUserSessionApiArg
        >({
            query: (queryArg) => ({ url: '/api/sso/session' }),
        }),
        getUserActive: build.query<
            GetUserActiveApiResponse,
            GetUserActiveApiArg
        >({
            query: (queryArg) => ({
                url: `/api/sso/internal/user/${queryArg.email}/active`,
            }),
        }),
        userEditPassword: build.mutation<
            UserEditPasswordApiResponse,
            UserEditPasswordApiArg
        >({
            query: (queryArg) => ({
                url: `/api/sso/internal/user/${queryArg.email}/password`,
                method: 'PUT',
                body: queryArg.editUserPasswordCommand,
            }),
        }),
        deprecatedUserEditPassword: build.mutation<
            DeprecatedUserEditPasswordApiResponse,
            DeprecatedUserEditPasswordApiArg
        >({
            query: (queryArg) => ({
                url: '/api/sso/internal/user/password',
                method: 'PUT',
                body: queryArg.editUserPasswordCommand,
            }),
        }),
        postApiLogout: build.mutation<
            PostApiLogoutApiResponse,
            PostApiLogoutApiArg
        >({
            query: () => ({ url: '/api/sso/logout', method: 'POST' }),
        }),
    }),
    overrideExisting: false,
})
export { injectedRtkApi as authSlice }
export type LoginApiResponse = /** status 200 OK */ {
    user?: UserDto
    refreshToken?: string
    refreshTokenExpiration?: number
}
export type LoginApiArg = {
    /** Json body */
    body: {
        username?: string
        password?: string
    }
}
export type RefreshTokenApiResponse = /** status 200 OK */ {
    refreshToken?: string
    refreshTokenExpiration?: number
}
export type RefreshTokenApiArg = {
    /** refresh token, you got from login endpoint */
    refreshToken: string
}
export type InvalidateTokenApiResponse = /** status 200 OK */ {
    code?: number
    message?: string
}
export type InvalidateTokenApiArg = {
    /** refresh token, you got from login endpoint */
    refreshToken: string
}
export type UserRegisterApiResponse =
    /** status 200 Created user data */ RegisterUserPresenter
export type UserRegisterApiArg = {
    registerUserCommand: RegisterUserCommand
}
export type GetUserRegisterConfirmApiResponse = unknown
export type GetUserRegisterConfirmApiArg = {
    token: string
    lang?: string
}
export type GetUserSessionApiResponse =
    /** status 200 Get session */ GetSessionPresenter
export type GetUserSessionApiArg = {
    _locale: string
}
export type GetUserActiveApiResponse =
    /** status 200 Check if user is active */ GetUserActiveStatusPresenter
export type GetUserActiveApiArg = {
    email: string
}
export type UserEditPasswordApiResponse = unknown
export type UserEditPasswordApiArg = {
    email: string
    editUserPasswordCommand: EditUserPasswordCommand
}
export type DeprecatedUserEditPasswordApiResponse = unknown
export type DeprecatedUserEditPasswordApiArg = {
    editUserPasswordCommand: EditUserPasswordCommand
}
export type PostApiLogoutApiResponse = unknown
export type PostApiLogoutApiArg = void
export type UserDto = {
    name?: string
    surname?: string
    email?: string
    roles?: string[]
}
export type RegisterUserPresenter = {
    userId?: string
}
export type RegisterUserCommand = {
    dateOfBirth?: string | null
    fromMaintenance?: boolean
    email: string
    password: string
    name: string
    surname: string
    isRequitedTermsAccepted: boolean
    locale: 'en' | 'pl' | 'uk'
}
export type GetSessionPresenter = {
    name?: string
    surname?: string
    email?: string
}
export type GetUserActiveStatusPresenter = {
    isActive?: boolean
    isConfirmed?: boolean
}
export type EditUserPasswordCommand = {
    email: string
    newPassword: string
}
export const {
    useLoginMutation,
    useRefreshTokenQuery,
    useLazyRefreshTokenQuery,
    useInvalidateTokenQuery,
    useLazyInvalidateTokenQuery,
    useUserRegisterMutation,
    useGetUserRegisterConfirmQuery,
    useLazyGetUserRegisterConfirmQuery,
    useGetUserSessionQuery,
    useLazyGetUserSessionQuery,
    useGetUserActiveQuery,
    useLazyGetUserActiveQuery,
    useUserEditPasswordMutation,
    useDeprecatedUserEditPasswordMutation,
    usePostApiLogoutMutation,
} = injectedRtkApi
